import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, Paper, Chip, IconButton, Tooltip, TextField,
  InputAdornment, FormControl, Select, MenuItem, InputLabel,
  CircularProgress, Typography, Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { travelerScreeningAPI } from '../../services/api';
import HealthPassCard from './HealthPassCard';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';

interface Screening {
  id: number;
  full_name?: string;
  id_number?: string;
  nationality?: string;
  entry_country?: string;
  arrival_date?: string;
  risk_level?: string;
  status?: string;
  temperature?: number;
  phone_number?: string;
  pass_record?: Record<string, unknown>;
  created_at?: string;
}

const maskPhoneNumber = (phone?: string): string => {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length <= 4) return digits;
  const last4 = digits.slice(-4);
  return `${'*'.repeat(digits.length - 4)}${last4}`;
};

interface Props {
  refresh: number;
  onAddNew: () => void;
}

const PassengerList: React.FC<Props> = ({ refresh, onAddNew }) => {
  const [rows,      setRows]      = useState<Screening[]>([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(0);
  const [rowsPerPage] = useState(20);
  const [search,    setSearch]    = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter,    setFilter]    = useState('');
  const [loading,   setLoading]   = useState(false);
  const [viewPass,  setViewPass]  = useState<Screening | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await travelerScreeningAPI.listScreenings({
        skip:       page * rowsPerPage,
        limit:      rowsPerPage,
        risk_level: filter || undefined,
        search:     debouncedSearch || undefined,
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter, debouncedSearch, refresh]);

  useEffect(() => { load(); }, [load]);

  const riskChip = (level?: string) => (
    <Chip
      size="small"
      label={level === 'cleared' ? 'Cleared' : level === 'high_risk' ? 'High Risk' : 'Pending'}
      color={level === 'cleared' ? 'success' : level === 'high_risk' ? 'error' : 'default'}
    />
  );

  const handleDownload = (id: number) => {
    const url = travelerScreeningAPI.getPassPdfUrl(id);
    const token = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `health_pass_${id}.pdf`;
        a.click();
      });
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search name, passport, nationality…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Risk Level</InputLabel>
          <Select
            value={filter}
            label="Risk Level"
            onChange={e => {
              setFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="cleared">Cleared</MenuItem>
            <MenuItem value="high_risk">High Risk</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto' }}>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={onAddNew}>
            Add Passenger
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>#</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Passport No.</TableCell>
              <TableCell>Nationality</TableCell>
              <TableCell>Entry Country</TableCell>
              <TableCell>Arrival Date</TableCell>
              <TableCell>Temp (°C)</TableCell>
              <TableCell>Risk</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {debouncedSearch ? 'No matching screenings.' : 'No screenings yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.full_name || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{maskPhoneNumber(row.phone_number)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{row.id_number || '—'}</TableCell>
                  <TableCell>{row.nationality || '—'}</TableCell>
                  <TableCell>{row.entry_country || '—'}</TableCell>
                  <TableCell>{row.arrival_date || '—'}</TableCell>
                  <TableCell sx={{ color: (row.temperature ?? 0) >= 38 ? 'error.main' : 'inherit', fontWeight: (row.temperature ?? 0) >= 38 ? 'bold' : 'normal' }}>
                    {row.temperature != null ? `${row.temperature}°C` : '—'}
                  </TableCell>
                  <TableCell>{riskChip(row.risk_level)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Pass">
                      <IconButton size="small" onClick={() => setViewPass(row)} disabled={!row.pass_record}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download PDF">
                      <IconButton size="small" onClick={() => handleDownload(row.id)} disabled={!row.pass_record}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPageOptions={[20]}
        />
      </TableContainer>

      {/* Pass preview dialog */}
      <Dialog open={Boolean(viewPass)} onClose={() => setViewPass(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Health Pass — {viewPass?.full_name}</DialogTitle>
        <DialogContent>
          {viewPass?.pass_record && (
            <HealthPassCard passRecord={viewPass.pass_record as unknown as Parameters<typeof HealthPassCard>[0]['passRecord']} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewPass(null)}>Close</Button>
          {viewPass && (
            <Button startIcon={<DownloadIcon />} onClick={() => handleDownload(viewPass.id)}>
              Download PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PassengerList;
